import ApolloClient, { ApolloError } from 'apollo-client';
import { ApolloLink, split, concat, Operation, NextLink } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import { createUploadLink } from 'apollo-upload-client';
import { WebSocketLink } from 'apollo-link-ws';
import { NormalizedCacheObject, InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { persistCache } from 'apollo-cache-persist';
import { onError } from 'apollo-link-error';

import introspectionQueryResultData from '@/constants/fragmentTypes.json';

export interface GraphQLRequestHeaders {
  organizationId?: string;
}

export async function createApolloClient(
  httpEndpointUrl: string,
  wsEndpointUrl: string,
  headersCallback: () => GraphQLRequestHeaders,
  errorHandler: (error: ApolloError) => Promise<void>,
): Promise<ApolloClient<NormalizedCacheObject>> {
  const link = getLink(httpEndpointUrl, wsEndpointUrl, headersCallback, errorHandler);
  const cache = getCache();
  await persistCache({
    cache,
    storage: window.localStorage as any,
  });
  return new ApolloClient({
    link,
    cache,
    connectToDevTools: true,
  });
}

function getLink(
  httpEndpointUrl: string,
  wsEndpointUrl: string,
  headersCallback: () => GraphQLRequestHeaders,
  errorHandler: (error: ApolloError) => Promise<void>,
): ApolloLink {
  const wsLink = getWSLink(wsEndpointUrl);
  const httpLink = getHTTPLink(httpEndpointUrl, headersCallback);
  const errorLink = getErrorLink(errorHandler);
  return split(
    // split based on operation type
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    wsLink,
    ApolloLink.from([errorLink, httpLink]),
  );
}

function getHTTPLink(
  endpointUrl: string,
  headersCallback: () => GraphQLRequestHeaders,
): ApolloLink {
  const httpLink = createUploadLink({
    uri: `${endpointUrl}/graphql`,
    credentials: 'include',
  });
  const middleware = new ApolloLink(
    (operation: Operation, forward?: NextLink) => {
      const headers = headersCallback();
      operation.setContext({
        headers,
      });
      return (forward != undefined)
        ? forward(operation)
        : /* tslint:disable */ null /* tslint:enable */;
    },
  );
  return concat(middleware, httpLink);
}

function getErrorLink(
  errorHandler: (error: ApolloError) => Promise<void>,
): ApolloLink {
  return onError(({ networkError, graphQLErrors }) => {
    if (graphQLErrors || networkError) {
      const apolloError = new ApolloError({
        networkError,
        graphQLErrors,
      });
      errorHandler(apolloError);
    }
  });
}

function getWSLink(endpointUrl: string): WebSocketLink {
  return new WebSocketLink({
    uri: `${endpointUrl}/subscriptions`,
    options: {
      reconnect: true,
      timeout: 30000,
    },
  });
}

function getCache(): InMemoryCache {
  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData,
  });
  return new InMemoryCache({
    fragmentMatcher,
    cacheRedirects: {
      Query: {
        light: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'Light', id }),
        group: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'Group', id }),
        room: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'Room', id }),
        scheduledEvent: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'ScheduledEvent', id }),
        building: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'Building', id }),
        member: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'BuildingUser', id }),
        invitation: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'Invitation', id }),
        scene: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'Scene', id }),
        accessory: (_, { id }, { getCacheKey }) =>
          getCacheKey({ __typename: 'Accessory', id }),
      },
    },
  });
}
